<template>
  <div class="admin-notice-detail">
    <!-- 로딩 -->
    <div v-if="loading" class="loading-container">
      <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
    </div>

    <template v-else-if="notice">
      <!-- 공지사항 내용 -->
      <div class="notice-container">
        <!-- 카테고리 & 제목 -->
        <div class="notice-header-section">
          <div class="category-row">
            <v-chip :color="getCategoryColor(notice.nc_category)" size="small" variant="flat">
              {{ getCategoryText(notice.nc_category) }}
            </v-chip>
            <v-chip v-if="notice.nc_type === 'ADMIN'" color="grey-darken-2" size="small" variant="outlined" class="ml-2">
              전체
            </v-chip>
          </div>
          <h1 class="notice-title">{{ notice.nc_title }}</h1>
          <div class="notice-meta-row">
            <span class="author">{{ notice.nc_type === 'ADMIN' ? '관리자' : notice.nc_sisul_name || '-' }}</span>
            <span class="divider">|</span>
            <span class="date">{{ formatDate(notice.nc_wdate) }}</span>
            <span class="divider">|</span>
            <span class="hit">조회 {{ notice.nc_hit }}</span>
          </div>
        </div>

        <!-- 본문 -->
        <div class="notice-body ql-editor" v-html="notice.nc_content"></div>

        <!-- 첨부파일 -->
        <div v-if="files.length > 0" class="notice-files">
          <div class="files-title">
            <v-icon size="16">mdi-attachment</v-icon>
            첨부파일 {{ files.length }}개
          </div>
          <div class="files-grid">
            <div v-for="file in files" :key="file.nf_idx" class="file-chip" @click="downloadFile(file)">
              <v-icon size="14">{{ getFileIcon(file.nf_type) }}</v-icon>
              <span>{{ file.nf_original_name }}</span>
            </div>
          </div>
        </div>

        <!-- 액션 버튼 -->
        <div class="action-row" v-if="canEdit">
          <v-btn variant="outlined" size="small" @click="goToEdit">
            <v-icon start size="small">mdi-pencil</v-icon>수정
          </v-btn>
          <v-btn variant="outlined" color="error" size="small" @click="confirmDelete">
            <v-icon start size="small">mdi-delete</v-icon>삭제
          </v-btn>
        </div>
      </div>

      <!-- 공지사항 목록 -->
      <div class="other-notices">
        <div class="section-title">공지사항 목록</div>
        <div class="notice-list">
          <div 
            v-for="item in otherNotices" 
            :key="item.nc_idx" 
            class="notice-list-item"
            :class="{ active: item.nc_idx === notice.nc_idx }"
            @click="goToNotice(item)"
          >
            <v-chip :color="getCategoryColor(item.nc_category)" size="x-small" variant="flat" class="mr-2">
              {{ getCategoryShort(item.nc_category) }}
            </v-chip>
            <span class="item-title">{{ item.nc_title }}</span>
            <span class="item-author">{{ item.nc_type === 'ADMIN' ? '관리자' : item.nc_sisul_name || '-' }}</span>
            <span class="item-date">{{ formatListDate(item.nc_wdate) }}</span>
          </div>
        </div>
      </div>
    </template>

    <!-- 에러 -->
    <div v-else class="error-container">
      <v-icon size="64" color="grey-lighten-1">mdi-alert-circle-outline</v-icon>
      <p>공지사항을 찾을 수 없습니다.</p>
      <v-btn color="primary" variant="tonal" @click="goBack">목록으로</v-btn>
    </div>

    <!-- 삭제 확인 다이얼로그 -->
    <v-dialog v-model="deleteDialog" max-width="360">
      <v-card>
        <v-card-title class="text-body-1 font-weight-bold">공지사항 삭제</v-card-title>
        <v-card-text>이 공지사항을 삭제하시겠습니까?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="deleteDialog = false">취소</v-btn>
          <v-btn color="error" variant="flat" @click="deleteNotice" :loading="deleting">삭제</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" location="top">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';

interface Notice {
  nc_idx: number;
  nc_type: 'ADMIN' | 'CENTER';
  nc_category: string;
  nc_sisul_code: string | null;
  nc_sisul_name: string | null;
  nc_writer_name: string;
  nc_title: string;
  nc_content: string;
  nc_hit: number;
  nc_wdate: string;
}

interface NoticeFile {
  nf_idx: number;
  nf_original_name: string;
  nf_size: number;
  nf_type: string;
}

const route = useRoute();
const router = useRouter();
const store = useStore();

const loading = ref(true);
const notice = ref<Notice | null>(null);
const files = ref<NoticeFile[]>([]);
const otherNotices = ref<Notice[]>([]);
const deleteDialog = ref(false);
const deleting = ref(false);
const snackbar = ref({ show: false, text: '', color: 'info' });

const user = computed(() => store.state.user);
const canEdit = computed(() => {
  if (!notice.value || !user.value) return false;
  if (user.value.m_role === 'admin') return true;
  if (notice.value.nc_type === 'CENTER' && notice.value.nc_sisul_code === user.value.m_sisul_code) return true;
  return false;
});

const loadNotice = async () => {
  const ncIdx = route.params.ncIdx;
  loading.value = true;
  
  try {
    const [detailRes, listRes] = await Promise.all([
      axios.get(`/api/notice/detail/${ncIdx}`, { withCredentials: true }),
      axios.get('/api/notice/list', { params: { page: 1, limit: 20 }, withCredentials: true })
    ]);
    
    if (detailRes.data.success) {
      notice.value = detailRes.data.notice;
      files.value = detailRes.data.files || [];
    }
    
    if (listRes.data.success) {
      otherNotices.value = listRes.data.notices;
    }
  } catch (error) {
    console.error('공지사항 로드 오류:', error);
  } finally {
    loading.value = false;
  }
};

const confirmDelete = () => { deleteDialog.value = true; };

const deleteNotice = async () => {
  if (!notice.value) return;
  deleting.value = true;
  try {
    const response = await axios.delete(`/api/notice/delete/${notice.value.nc_idx}`, { withCredentials: true });
    if (response.data.success) {
      showSnackbar('삭제되었습니다.', 'success');
      setTimeout(() => router.push('/admin/notice'), 1000);
    }
  } catch (error) {
    showSnackbar('삭제에 실패했습니다.', 'error');
  } finally {
    deleting.value = false;
    deleteDialog.value = false;
  }
};

const downloadFile = (file: NoticeFile) => { window.open(`/api/notice/download/${file.nf_idx}`, '_blank'); };
const goBack = () => { router.push('/admin/notice'); };
const goToEdit = () => { if (notice.value) router.push(`/admin/notice/edit/${notice.value.nc_idx}`); };
const goToNotice = (item: Notice | null) => { if (item) router.push(`/admin/notice/${item.nc_idx}`); };

const formatDate = (date: string) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};

const formatShortDate = (date: string) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${d.getMonth()+1}/${d.getDate()}`;
};

const formatListDate = (date: string) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')}`;
};

const getFileIcon = (type: string) => {
  if (!type) return 'mdi-file';
  if (type.includes('image')) return 'mdi-file-image';
  if (type.includes('pdf')) return 'mdi-file-pdf-box';
  return 'mdi-file';
};

const getCategoryColor = (c: string) => ({ NOTICE: 'primary', UPDATE: 'info', EVENT: 'success', URGENT: 'error' }[c] || 'grey');
const getCategoryText = (c: string) => ({ NOTICE: '공지사항', UPDATE: '업데이트', EVENT: '이벤트', URGENT: '긴급' }[c] || c);
const getCategoryShort = (c: string) => ({ NOTICE: '공지', UPDATE: '업데이트', EVENT: '이벤트', URGENT: '긴급' }[c] || c);
const showSnackbar = (text: string, color = 'info') => { snackbar.value = { show: true, text, color }; };

onMounted(() => { loadNotice(); });
</script>

<style lang="scss" scoped>
@import '@vueup/vue-quill/dist/vue-quill.snow.css';

.admin-notice-detail {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px 16px;
}

.loading-container, .error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: #999;
  p { margin: 16px 0; }
}

.notice-container {
  background: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
}

.notice-header-section {
  padding: 20px;
  border-bottom: 1px solid #eee;
  
  .category-row { margin-bottom: 12px; }
  
  .notice-title {
    font-size: 22px;
    font-weight: 700;
    color: #222;
    line-height: 1.4;
    margin: 0 0 12px 0;
  }
  
  .notice-meta-row {
    font-size: 13px;
    color: #666;
    
    .divider { margin: 0 8px; color: #ddd; }
  }
}

.notice-body {
  padding: 24px 20px;
  min-height: 200px;
  line-height: 1.8;
  font-size: 15px;
  color: #333;
  
  :deep(img) { max-width: 100%; height: auto; border-radius: 4px; }
}

.notice-files {
  padding: 16px 20px;
  background: #f9f9f9;
  border-top: 1px solid #eee;
  
  .files-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #555;
    margin-bottom: 10px;
  }
  
  .files-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .file-chip {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: white;
    border: 1px solid #ddd;
    border-radius: 20px;
    font-size: 12px;
    color: #333;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover { background: #f0f0f0; border-color: #bbb; }
  }
}

.action-row {
  display: flex;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
  justify-content: flex-end;
}

.other-notices {
  margin-top: 32px;
  
  .section-title {
    font-size: 16px;
    font-weight: 700;
    color: #333;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 2px solid #333;
  }
  
  .notice-list-item {
    display: flex;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #eee;
    cursor: pointer;
    transition: background 0.2s;
    
    &:hover { background: #fafafa; }
    &.active { background: #e3f2fd; }
    
    .item-title {
      flex: 1;
      font-size: 14px;
      color: #333;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .item-author {
      font-size: 12px;
      color: #666;
      margin-left: 12px;
      white-space: nowrap;
    }
    
    .item-date {
      font-size: 12px;
      color: #999;
      margin-left: 12px;
      white-space: nowrap;
    }
  }
}
</style>
